import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { CompanyAdminConfiguration } from 'src/app/shared/models/company-admin-configuration';
import { FormConfiguration } from '../models/form-configuration';
import { ICompanyAdminConfigurationService } from '../services/company-admin-configuration/company.admin.configuration.service.interface';
import { KendoAlertService } from '../services/kendo-alerts.service';
import { Store } from './store.base';

@Injectable()
export class CompanyAdminConfigurationStore extends Store<CompanyAdminConfiguration> {
  private bkCompanyKey: string;

  constructor(
    private companyAdminConfigurationService: ICompanyAdminConfigurationService,
    private kendoAlertService: KendoAlertService
  ) {
      super();
  }

  public load(bkCompanyKey: string): Observable<CompanyAdminConfiguration> {
    if (this.bkCompanyKey === bkCompanyKey) {
      return this.get();
    }

    this.loading.next(true);

    this.companyAdminConfigurationService.get(bkCompanyKey).subscribe({
      next: config => {
        this.cachedItem.next(config);
        this.bkCompanyKey = bkCompanyKey;
        this.loading.next(false);
        if (!config) {
          this.kendoAlertService.showErrorAlert("alerts.companyAdminConfigurationNotFound");
        }
        else if (!config.vatRate) {
          this.kendoAlertService.showErrorAlert("alerts.vatRateNotFound");
        }
      },
      error: error => {
        this.loading.next(false);
        this.kendoAlertService.showErrorAlert(error);
      }
    });

    return this.get();
  }

  public getFormConfiguration(formName: string): Observable<FormConfiguration> {
    return this.get().pipe(
      concatMap(companyAdminConfiguration => {
        const formConfig = companyAdminConfiguration?.formConfigurations?.find(fc => fc.name === formName);

        if (formConfig && formConfig.fieldConfigurations) {
          const hasInvalidOrder = formConfig.fieldConfigurations.some(fc => fc.order <= 0);
          const orders = formConfig.fieldConfigurations.map(fc => fc.order);
          const hasDuplicates = new Set(orders).size !== orders.length;
          const isValid = !hasInvalidOrder && !hasDuplicates;

          const sortedFieldConfigurations = [...formConfig.fieldConfigurations].sort((a, b) => a.order - b.order);
          const sortedFormConfig: FormConfiguration = {
            ...formConfig,
            fieldConfigurations: sortedFieldConfigurations,
            isValid: isValid
          };
          return of(sortedFormConfig);
        }

        return of({ name: formName, isValid: false } as FormConfiguration);
      })
    );
  }
}
